import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { zxcvbn, ZxcvbnOptions } from '@zxcvbn-ts/core';
import { ZxcvbnResult } from '@zxcvbn-ts/core/src/types';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { zxcvbnLoadOptions } from '../../../util/password-strength/zxcvbn-utils';
import { BlockedWordsMatcher } from './zxcvbn-blocked-words.matcher';
import { LeadingTrailingSpacesMatcher } from './zxcvbn-leading-trailing-spaces.matcher';
import { Logger } from '../../../feature/logger/logger';
import { useTranslation } from 'next-i18next';

export type PasswordStrengthMeterProps = {
  password: string;
  language: string;
  blockedWords?: string[];
};

const checkIcon = getDesignSystemIcon('icon-check-16.svg');

const PasswordStrengthMeter = ({ password = '', language = 'en', blockedWords = [] }: PasswordStrengthMeterProps) => {
  const { t } = useTranslation('shared');

  const zxcvbnOptionsRef = useRef<any | null>(null);
  const [zxcvbnConfigured, setZxcvbnConfigured] = useState(false);
  const [passwordEvaluationResult, setPasswordEvaluationResult] = useState<ZxcvbnResult>(() => zxcvbn('') as ZxcvbnResult);
  const componentMounted = useRef(true);

  const normalizedLanguage = useMemo(() => {
    if (language.includes('-') && language.length > 2) {
      Logger.info('💡 Language is in the format of language-country, using only the language part', language);
      return language.split('-')[0]?.toLowerCase();
    }
    return language?.toLowerCase();
  }, [language]);

  useEffect(() => {
    // Evaluate the trimmed password
    if (zxcvbnOptionsRef.current) {
      if (blockedWords && blockedWords.length > 0) {
        zxcvbnOptionsRef.current.dictionary.blockedWords = blockedWords;
      }

      ZxcvbnOptions.setOptions(zxcvbnOptionsRef.current);
      setPasswordEvaluationResult(zxcvbn(password) as ZxcvbnResult);
    }
  }, [password, blockedWords]);

  useEffect(() => {
    if (!zxcvbnConfigured) {
      zxcvbnLoadOptions(normalizedLanguage).then((options) => {
        zxcvbnOptionsRef.current = options;
        ZxcvbnOptions.setOptions(zxcvbnOptionsRef.current);

        zxcvbnOptionsRef.current.dictionary.blockedWords = blockedWords;
        if (typeof ZxcvbnOptions.matchers['blockedWords'] === 'undefined') {
          ZxcvbnOptions.addMatcher('blockedWords', BlockedWordsMatcher);
        }

        ZxcvbnOptions.addMatcher('leadingTrailingSpaces', LeadingTrailingSpacesMatcher);

        if (componentMounted.current) {
          setZxcvbnConfigured(true);
          setPasswordEvaluationResult(zxcvbn(password) as ZxcvbnResult);
        }
      });
    } else if (blockedWords && blockedWords.length > 0) {
      if (componentMounted.current) {
        zxcvbnOptionsRef.current.dictionary.blockedWords = blockedWords;
        ZxcvbnOptions.setOptions(zxcvbnOptionsRef.current);
        setPasswordEvaluationResult(zxcvbn(password) as ZxcvbnResult);
      }
    }
    return () => {
      componentMounted.current = false;
    };
  }, []);

  const passwordStrengthWord = [
    t('shared_passwordStrength_veryInsecure_label'),
    t('shared_passwordStrength_insecure_label'),
    t('shared_passwordStrength_weak_label'),
    t('shared_passwordStrength_ok_label'),
    t('shared_passwordStrength_veryStrong_label'),
  ];

  const getStrengthBarStyles = useCallback((score = 0, length = 0) => {
    const styles = {
      width: '0',
      backgroundColor: '#fff',
    };

    const COLOR_CODE_MAP = ['#d33c3b', '#d33c3b', '#d33c3b', '#8fb36f', '#51773b'];
    styles.backgroundColor = COLOR_CODE_MAP[score];

    if (length === 0) {
      styles.width = '0%';
    } else {
      styles.width = `${(score + 1) * 20}%`;
    }

    return styles;
  }, []);

  return (
    <div className="mb-4">
      <div className="mb-2">
        <div className="flex justify-between">
          <div className="h-2.5 w-full border border-primary-black bg-primary-white">
            <div
              className="h-full transition-all duration-200 ease-in-out"
              style={getStrengthBarStyles(passwordEvaluationResult?.score, password.length)}
            />
          </div>
        </div>
      </div>

      <div className="mt-2 text-sm text-secondary-dark flex gap-2 items-center">
        <div aria-live="polite" aria-atomic="true">
          {password?.length > 0 && (
            <>
              <span>{passwordStrengthWord[passwordEvaluationResult?.score]}</span>
              {passwordEvaluationResult?.feedback?.suggestions?.length > 0 && (
                <div className="mt-4 text-sm">
                  <div className="font-bold">{t('shared_passwordStrength_suggestions_label')}</div>
                  <ul className="mt-2 list-disc pl-4">
                    {passwordEvaluationResult?.sequence?.find(
                      (s) => s.pattern === 'blockedWords' || s.dictionaryName === 'blockedWords'
                    ) && (
                      <li key="blocked-words" className="mt-1">
                        {t('shared_passwordStrength_blockedWords_warning_label')}
                      </li>
                    )}
                    {passwordEvaluationResult?.sequence?.find((s) => s.pattern === 'leadingTrailingSpaces') && (
                      <li key="leading-trailing-spaces" className="mt-1">
                        {t('shared_passwordStrength_noLeadingOrTrailingSpace_warning_label')}
                      </li>
                    )}
                    {passwordEvaluationResult?.feedback?.suggestions?.map((suggestion) => (
                      <li key={suggestion} className="mt-1">
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        {password?.length > 0 && passwordEvaluationResult?.score >= 3 && (
          <svg className="-mt-1 ml-1 inline-block h-4 w-4 fill-current text-green-dark">
            <use xlinkHref={checkIcon + '#img'} />
          </svg>
        )}
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;
